<template>
    <v-card flat style="border-top: 1px solid lightgray">
        <v-overlay :value="loadingClientList">
            <v-progress-circular indeterminate size="64"></v-progress-circular>
        </v-overlay>
        <v-treeview
            :items="clientTree"
            :open.sync="openNodes"
            :active.sync="activeNodes"
            :search="searchClientString"
            :filter="filterTree"
            activatable
            open-on-click
            item-key="id"
            item-text="name"
            dense
            class="client-tree"
            @update:active="handleNodeClick"
        >
            <template v-slot:prepend="{ item, open }">
                <v-icon v-if="item.level === 1" small>
                    {{ open ? 'mdi-folder-open' : 'mdi-folder' }}
                </v-icon>
                <v-icon v-else-if="item.level === 2" small>
                    {{ open ? 'mdi-folder-open-outline' : 'mdi-folder-outline' }}
                </v-icon>
                <v-icon v-else small>mdi-store</v-icon>
            </template>
            <template v-slot:label="{ item }">
                <span
                    :class="[
                        'tree-label',
                        `level-${item.level}`,
                        { 'shop-item': item.level === 3 }
                    ]"
                >
                    {{ item.name }}
                    <!-- Shop count for groups -->
                    <span v-if="item.level === 1 || item.level === 2" class="shop-count ml-2">
                        ({{ item.shopCount }})
                    </span>
                    <!-- Additional info for shops -->
                    <span v-if="item.level === 3" class="shop-info">
                        <span v-if="item.country_code" class="ml-2">
                            <country-flag :country="item.country_code" size="small" />
                        </span>
                        <span v-if="item.shop_address" class="ml-2 text-caption grey--text">
                            {{ item.shop_address }}
                        </span>
                        <span v-if="item.business_type" class="ml-2 business-type">
                            {{ item.business_type }}
                        </span>
                        <span class="tags-container">
                            <span v-for="tag in item.tags" :key="tag" class="tag-chip">
                                {{ tag }}
                            </span>
                        </span>
                    </span>
                </span>
            </template>
        </v-treeview>
    </v-card>
</template>

<script>
import Store from "@/store";
import { mapState, mapGetters } from "vuex";
import CountryFlag from "vue-country-flag";
import { PermissionsConstants } from "@/permissions";

export default {
    name: "client-list",
    components: {
        CountryFlag
    },
    data() {
        return {
            openNodes: [],
            activeNodes: []
        };
    },
    mounted() {
        // Load all clients for tree view (no pagination)
        Store.dispatch("ClientStore/getClientsList", {
            options: null,
            search: this.searchClientString
        });
    },
    computed: {
        ...mapState("ClientStore", [
            "searchClientString",
            "loadingClientList",
            "activeClient",
            "clients",
            "totalClientCount"
        ]),
        ...mapState(["user"]),
        ...mapGetters("ClientStore", ["clientCount"]),
        ...mapGetters(["hasPermissionForAction"]),
        PermissionsConstants() {
            return PermissionsConstants;
        },
        clientTree() {
            const tree = [];
            const nameMap = new Map();

            for (const client of this.clients) {
                const clientName = client.name || "-";
                const chainName = client.chain_name || "-";
                const shopName = client.shop_name || "-";

                // Level 1: Client Name
                if (!nameMap.has(clientName)) {
                    nameMap.set(clientName, {
                        id: `name-${clientName}`,
                        name: clientName,
                        level: 1,
                        children: [],
                        chainMap: new Map()
                    });
                    tree.push(nameMap.get(clientName));
                }

                const nameNode = nameMap.get(clientName);

                // Level 2: Chain Name
                if (!nameNode.chainMap.has(chainName)) {
                    nameNode.chainMap.set(chainName, {
                        id: `chain-${clientName}-${chainName}`,
                        name: chainName,
                        level: 2,
                        children: []
                    });
                    nameNode.children.push(nameNode.chainMap.get(chainName));
                }

                const chainNode = nameNode.chainMap.get(chainName);

                // Level 3: Shop Name (leaf node with client data)
                chainNode.children.push({
                    id: `shop-${client.id}`,
                    name: shopName,
                    level: 3,
                    clientId: client.id,
                    country_code: client.country_code,
                    shop_address: client.shop_address,
                    business_type: client.business_type,
                    tags: client.tags || []
                });
            }

            // Sort tree alphabetically at each level and calculate shop counts
            tree.sort((a, b) => a.name.localeCompare(b.name));
            tree.forEach(nameNode => {
                nameNode.children.sort((a, b) => a.name.localeCompare(b.name));
                let nameNodeShopCount = 0;
                nameNode.children.forEach(chainNode => {
                    chainNode.children.sort((a, b) => a.name.localeCompare(b.name));
                    // Level 2: count shops in this chain
                    chainNode.shopCount = chainNode.children.length;
                    nameNodeShopCount += chainNode.children.length;
                });
                // Level 1: total shops under this name
                nameNode.shopCount = nameNodeShopCount;
            });

            return tree;
        },
        filterTree() {
            return (item, search, textKey) => {
                if (!search) return true;
                const searchLower = search.toLowerCase();

                // Search in all levels
                if (item.name && item.name.toLowerCase().includes(searchLower)) {
                    return true;
                }

                // For leaf nodes, also check parent path
                if (item.level === 3 && item.clientId) {
                    const client = this.clients.find(c => c.id === item.clientId);
                    if (client) {
                        return (
                            (client.name && client.name.toLowerCase().includes(searchLower)) ||
                            (client.chain_name && client.chain_name.toLowerCase().includes(searchLower)) ||
                            (client.shop_name && client.shop_name.toLowerCase().includes(searchLower)) ||
                            (client.shop_address && client.shop_address.toLowerCase().includes(searchLower)) ||
                            (client.comment && client.comment.toLowerCase().includes(searchLower)) ||
                            (client.country_estonian && client.country_estonian.toLowerCase().includes(searchLower)) ||
                            (client.country_english && client.country_english.toLowerCase().includes(searchLower))
                        );
                    }
                }

                return false;
            };
        }
    },
    watch: {
        searchClientString: {
            handler(newVal) {
                if (newVal && newVal.length > 0) {
                    // Auto-expand all nodes when searching
                    this.expandAllMatching();
                }
            }
        },
        activeClient: {
            handler(newVal) {
                if (newVal && newVal.id) {
                    this.activeNodes = [`shop-${newVal.id}`];
                }
            },
            immediate: true
        }
    },
    methods: {
        handleNodeClick(activeIds) {
            if (!activeIds || activeIds.length === 0) return;

            const nodeId = activeIds[0];

            // Only handle shop-level clicks (level 3)
            if (nodeId && nodeId.startsWith('shop-')) {
                const clientId = nodeId.replace('shop-', '');

                if (!this.hasPermissionForAction(PermissionsConstants.CLIENT, PermissionsConstants.VIEW)) {
                    return;
                }

                if (this.$route.name !== "client-view") {
                    this.$router.push({ name: "client-view", params: { clientId: clientId } });
                    return;
                }

                if (this.$route.name === "client-view" && clientId !== this.$route.params.clientId) {
                    this.$router.push({ name: "client-view", params: { clientId: clientId } });
                    Store.dispatch("ClientStore/getClientData", clientId);
                }
            }
        },
        expandAllMatching() {
            // Expand all nodes to show search results
            const nodesToOpen = [];

            const collectNodeIds = (nodes) => {
                for (const node of nodes) {
                    if (node.children && node.children.length > 0) {
                        nodesToOpen.push(node.id);
                        collectNodeIds(node.children);
                    }
                }
            };

            collectNodeIds(this.clientTree);
            this.openNodes = nodesToOpen;
        },
        getItemCountry(item) {
            if (item.country_estonian) {
                return item.country_estonian;
            }
            if (item.country_english) {
                return item.country_english;
            }
            return "-";
        }
    }
};
</script>

<style scoped>
.client-tree {
    overflow-y: auto;
}

.shop-item {
    cursor: pointer;
    display: flex;
    align-items: center;
    width: 100%;
}

.level-1 {
    font-weight: 500;
}

.shop-count {
    color: #888;
    font-size: 12px;
    font-weight: normal;
}

.shop-info {
    display: inline-flex;
    align-items: center;
    flex: 1;
}

.business-type {
    font-size: 11px;
    color: #666;
    background-color: #e8e8e8;
    padding: 1px 6px;
    border-radius: 3px;
}

.tags-container {
    margin-left: auto;
    padding-left: 12px;
}

.tag-chip {
    font-size: 10px;
    color: #666;
    background-color: #e0e0e0;
    padding: 1px 6px;
    border-radius: 10px;
    margin-left: 4px;
}

::v-deep .v-treeview-node__root {
    min-height: 25px;
    padding-left: 8px;
    border-bottom: 1px solid rgba(0, 0, 0, 0.06);
}

::v-deep .v-treeview-node__label {
    flex: 1;
    overflow: visible;
}

::v-deep .v-treeview-node__root:has(.level-1) {
    background-color: #f5f5f5;
}

::v-deep .v-treeview-node__root:has(.level-2) {
    background-color: #fafafa;
}

::v-deep .v-treeview-node__root:hover {
    background-color: rgba(0, 0, 0, 0.08);
}

::v-deep .v-treeview-node--active > .v-treeview-node__root {
    background-color: rgba(0, 0, 0, 0.12);
}

::v-deep .v-treeview-node__content {
    margin-left: 0;
}

::v-deep .v-treeview-node__prepend {
    min-width: 24px;
}

::v-deep .v-treeview-node__toggle {
    width: 20px;
}

::v-deep .v-treeview-node__level {
    width: 16px;
}

::v-deep .v-icon {
    font-size: 16px;
}
</style>
